import { type PersonOverlayAttributeKey } from "./personOverlayAttributeKeys";
import { isTeamOverlayFieldKey } from "./teamOverlayFields";

export type OverlayCardEntityKind = "person" | "position" | "team";

export type OverlayCatalogEntityType = "person" | "position" | "team";

export type OverlayCatalogEntry = PersonOverlayAttributeKey & {
  entityTypes: readonly OverlayCatalogEntityType[];
  systemReadOnly?: boolean;
  pickerExcluded?: boolean;
  /** Read-only Details facts — hidden from Org Design Settings attribute table. */
  settingsExcluded?: boolean;
  internal?: boolean;
  help?: string;
};

/** Person directory/profile location and employer — always read-only in Org Design. */
export const PERSON_SYSTEM_READ_ONLY_KEYS = new Set([
  "address",
  "city",
  "company",
  "country",
  "state",
  "timezone",
]);

/** Position/seat facts shown read-only in Details — not configurable in settings. */
export const POSITION_DETAILS_ONLY_KEYS = new Set([
  "position.status",
  "position.positionId",
  "position.seatDates",
]);

const POSITION_SYSTEM_READ_ONLY_KEYS = POSITION_DETAILS_ONLY_KEYS;

export const POSITION_NAME_CATALOG_ENTRY: PersonOverlayAttributeKey = {
  key: "position.name",
  kind: "SCALAR",
  label: "Position name",
};

/** Ensures position.name is present when the API catalog has not caught up yet. */
export const ensurePositionCatalogEntries = (
  positionCatalog: readonly PersonOverlayAttributeKey[]
): PersonOverlayAttributeKey[] => {
  const entries = [...positionCatalog];
  if (!entries.some((entry) => entry.key === POSITION_NAME_CATALOG_ENTRY.key)) {
    entries.push(POSITION_NAME_CATALOG_ENTRY);
  }
  return entries;
};

/** Name, description, and links stay in Team Details chrome, not the overlay catalog. */
export const TEAM_DETAILS_CHROME_KEYS = new Set([
  "team.name",
  "team.description",
  "team.links",
]);

export const PERSON_PROFILE_LOCATION_HELP =
  "Person directory location, not the position/seat location.";

export const PERSON_TIMEZONE_HELP =
  "Used for local-time display on the person profile. Stored with Address, City, State, and Country — not the position/seat location.";

export const DEFAULT_PERSON_EMPLOYMENT_FTE_HELP =
  "Default employment FTE attribute — not a custom attribute.";

export const DEFAULT_POSITION_NAME_HELP =
  "Default position name attribute — not a custom attribute.";

export const DEFAULT_POSITION_ESTABLISHED_FTE_HELP =
  "Default established position FTE attribute — not a custom attribute.";

const POSITION_SEAT_LOCATION_HELP =
  "Position/seat location, not the person's profile address.";

const PERSON_LOCATION_HELP_BY_KEY: Readonly<Record<string, string>> = {
  address: PERSON_PROFILE_LOCATION_HELP,
  city: PERSON_PROFILE_LOCATION_HELP,
  country: PERSON_PROFILE_LOCATION_HELP,
  state: PERSON_PROFILE_LOCATION_HELP,
};

/** Position keys that also overlay on person cards and appear in People settings. */
const POSITION_KEYS_ON_PERSON_AND_POSITION = new Set(["position.jobTitle"]);

export const isTeamDetailsChromeKey = (key: string): boolean =>
  TEAM_DETAILS_CHROME_KEYS.has(key);

export const isSystemLockedReadOnlyAttributeKey = (key: string): boolean =>
  PERSON_SYSTEM_READ_ONLY_KEYS.has(key) ||
  POSITION_SYSTEM_READ_ONLY_KEYS.has(key);

export const toPersonOverlayCatalogEntry = (
  entry: PersonOverlayAttributeKey
): OverlayCatalogEntry => ({
  ...entry,
  entityTypes: ["person"],
  systemReadOnly: PERSON_SYSTEM_READ_ONLY_KEYS.has(entry.key),
  ...(PERSON_LOCATION_HELP_BY_KEY[entry.key]
    ? { help: PERSON_LOCATION_HELP_BY_KEY[entry.key] }
    : {}),
  ...(entry.key === "timezone" ? { help: PERSON_TIMEZONE_HELP } : {}),
  ...(entry.key === "fte" ? { help: DEFAULT_PERSON_EMPLOYMENT_FTE_HELP } : {}),
});

export const toPositionOverlayCatalogEntry = (
  entry: PersonOverlayAttributeKey
): OverlayCatalogEntry => ({
  ...entry,
  entityTypes: POSITION_KEYS_ON_PERSON_AND_POSITION.has(entry.key)
    ? ["person", "position"]
    : ["position"],
  systemReadOnly: POSITION_SYSTEM_READ_ONLY_KEYS.has(entry.key),
  settingsExcluded: POSITION_DETAILS_ONLY_KEYS.has(entry.key),
  pickerExcluded: POSITION_DETAILS_ONLY_KEYS.has(entry.key),
  ...(entry.key === "position.location"
    ? { help: POSITION_SEAT_LOCATION_HELP }
    : {}),
  ...(entry.key === "position.fte"
    ? { help: DEFAULT_POSITION_ESTABLISHED_FTE_HELP }
    : {}),
  ...(entry.key === "position.name"
    ? { help: DEFAULT_POSITION_NAME_HELP }
    : {}),
});

export const toTeamOverlayCatalogEntry = (
  entry: PersonOverlayAttributeKey
): OverlayCatalogEntry => ({
  ...entry,
  entityTypes: ["team"],
  pickerExcluded: isTeamDetailsChromeKey(entry.key),
});

export const toOverlayCatalogEntries = ({
  personCatalog,
  positionCatalog,
  teamCatalog,
}: {
  personCatalog: readonly PersonOverlayAttributeKey[];
  positionCatalog: readonly PersonOverlayAttributeKey[];
  teamCatalog: readonly PersonOverlayAttributeKey[];
}): OverlayCatalogEntry[] => [
  ...personCatalog.map(toPersonOverlayCatalogEntry),
  ...ensurePositionCatalogEntries(positionCatalog).map(
    toPositionOverlayCatalogEntry
  ),
  ...teamCatalog
    .filter((entry) => !isTeamDetailsChromeKey(entry.key))
    .map(toTeamOverlayCatalogEntry),
];

type OverlayCatalogEntryLike = {
  key: string;
  pickerExcluded?: boolean | undefined;
  entityTypes?: readonly OverlayCatalogEntityType[] | undefined;
};

const isTeamOverlayKeyWithoutEntityTypes = (key: string): boolean =>
  !isTeamDetailsChromeKey(key) && isTeamOverlayFieldKey(key);

export const isPositionOverlayAttributeKey = (key: string): boolean =>
  /^position\.[a-z][a-zA-Z0-9]*$/.test(key);

/**
 * Settings-table key shown in an entity group. Overlay/API keys stay unchanged
 * (`position.jobTitle` on cards, Details, and policies).
 */
export const getOverlayCatalogDisplayKey = (
  key: string,
  entityType: OverlayCatalogEntityType
): string => {
  if (entityType === "person" && key === "position.jobTitle") {
    return "person.jobTitle";
  }
  return key;
};

/** Properties picker and card overlays for an org-chart entity kind. */
export const overlayEntryForCardEntity = <T extends OverlayCatalogEntryLike>(
  entry: T,
  entityKind: OverlayCardEntityKind
): boolean => {
  if (entry.pickerExcluded === true || isTeamDetailsChromeKey(entry.key)) {
    return false;
  }
  if (entry.entityTypes && entry.entityTypes.length > 0) {
    switch (entityKind) {
      case "person":
        return entry.entityTypes.includes("person");
      case "position":
        return entry.entityTypes.includes("position");
      case "team":
        return entry.entityTypes.includes("team");
      default: {
        const _exhaustive: never = entityKind;
        return _exhaustive;
      }
    }
  }
  switch (entityKind) {
    case "team":
      return isTeamOverlayKeyWithoutEntityTypes(entry.key);
    case "position":
      return isPositionOverlayAttributeKey(entry.key);
    case "person":
      return !isTeamOverlayKeyWithoutEntityTypes(entry.key);
    default: {
      const _exhaustive: never = entityKind;
      return _exhaustive;
    }
  }
};
